import React, { forwardRef } from 'react';
import { styled } from '@mui/material/styles';
import { COLORS } from '@/theme/colors';
import { T } from '@/theme/tokens';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

interface AppCardProps {
  intro?: string;
  title?: string;
  icon?: React.JSX.Element;
  // When false, the icon is rendered as-is (no 30×30 tinted tile) — for cards
  // that pass a full custom header block through `icon` (e.g. Action Center).
  iconTile?: boolean;
  headerRight?: React.JSX.Element;
  footerRight?: React.JSX.Element;
  className?: string;
  children: React.ReactNode;
  id?: string;
  draggable?: boolean;
  listeners?: SyntheticListenerMap;
  attributes?: React.HTMLAttributes<any> & { 'data-testid'?: string };
  style?: React.CSSProperties;
  cardBodyStyle?: React.CSSProperties;
}

export const AppCard = forwardRef<HTMLDivElement, AppCardProps>(
  (
    {
      intro,
      title,
      icon,
      iconTile = true,
      headerRight,
      footerRight,
      children,
      className,
      id,
      draggable = false,
      listeners,
      attributes,
      style,
      cardBodyStyle,
    },
    ref,
  ) => {
    return (
      <AppCardDiv
        data-intro={intro}
        className={className}
        ref={ref}
        style={style}
        {...attributes}>
        {draggable && listeners && (
          <DragHandle {...listeners}>
            <DragHandleIcon />
          </DragHandle>
        )}
        <StyledCardContent>
          {title && (
            <StyledCardHeader>
              <IconContainer>
                {icon && (iconTile ? <IconTile>{icon}</IconTile> : icon)}
                <TitleText title={title}>{title}</TitleText>
              </IconContainer>
              {headerRight && <div style={{ flex: 1 }}>{headerRight}</div>}
            </StyledCardHeader>
          )}

          <CardBody style={cardBodyStyle} className="AppCardBody">
            {children}
          </CardBody>

          {footerRight && (
            <StyledCardHeader style={{ borderTopWidth: 0 }}>
              <div style={{ flex: 1 }} />
              {footerRight}
            </StyledCardHeader>
          )}
        </StyledCardContent>
      </AppCardDiv>
    );
  },
);

const AppCardDiv = styled('div')(({ theme }) => ({
  borderWidth: 1,
  borderRadius: T.radius,
  borderStyle: 'solid',
  borderColor: T.border,
  boxShadow: T.cardShadow,
  backgroundColor: T.surface,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  boxSizing: 'border-box',
}));

const StyledCardContent = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
}));

const TitleText = styled('p')(({ theme }) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  margin: 0,
  fontWeight: 900,
  fontSize: 16,
  color: T.text,
}));

const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  fontWeight: 900,
  flex: 1,
  minWidth: 0,
  color: T.primary,
}));

// Design card-header icon sits in a 30×30 tinted tile (dc.html:120/197/286).
const IconTile = styled('span')({
  width: 30,
  height: 30,
  borderRadius: 8,
  background: T.primaryTint,
  color: T.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': { fontSize: 18 },
});

const CardBody = styled('div')(({ theme }) => ({
  width: '100%',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '18px 20px',
}));

const StyledCardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: '16px 20px',
  gap: 10,
  borderColor: T.divider,
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  alignItems: 'center',
  placeContent: 'space-between',
  width: '100%',
  justifyContent: 'space-between',
  fontWeight: 900,
  flexShrink: 0,
}));

export const DragHandle = styled('div')(({ theme }) => ({
  cursor: 'grab',
  left: 'calc(50% - 20px)',
  top: -12,
  position: 'absolute',
  color: COLORS.primary,
  opacity: 0.5,
  width: 40,
  height: 40,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 1,
  },
}));
